import type { BetProperty, IBet, XpertenBetProperty } from '../../@types/Bet.js';
import { type IBoard, isQuickPick, type QuickPick } from '../../@types/Board.js';
import type { ITSPayloadBet } from '../../@types/ITSPayload.js';
import { ITS_PRODUCT_ID, type Product, PRODUCT_NAME, type SubProduct } from '../../@types/Product.js';
import { getProductConfig, type ProductConfig } from '../../config/getProductConfig.js';
import { WagerError } from '../../error/WagerError.js';

// const addonProductName = {
//   [SPORTKRYSS]: 'SPORTJOKER'
// };

// const addonProducts: Product[] = ['Sportkryss'];

/**
 * Main bet class that every product specific bet instance should create a subclass of.
 * Contains logic that's common to every product.
 */
abstract class Bet<TBoard extends IBoard> implements IBet<TBoard> {
  private readonly _boards: TBoard[] = [];

  private readonly _betProperties = new Set<BetProperty>();

  private readonly _product: Product;

  private _rowPrice = 1;

  private _drawNumber = 0;

  private _numberOfDraws = 1;

  // used for branding etc EM-Tipset/VM-Tipset
  private _subProduct?: SubProduct;

  constructor(product: Product) {
    this._product = product;

    const [minPrice] = getProductConfig(product).rowPrice;

    this._rowPrice = minPrice;
  }

  get product(): Product {
    return this._product;
  }

  /**
   * Returns all boards for wager.
   *
   * @returns [Boards] | Returns an array with instances of Board
   */
  get boards(): TBoard[] {
    return this._boards;
  }

  /**
   * Returns set rowPrice for wager.
   *
   * @returns Number | The given betAmount for wager
   */
  get rowPrice(): number {
    return this._rowPrice;
  }

  get drawNumber(): number {
    return this._drawNumber;
  }

  get numberOfDraws(): number {
    return this._numberOfDraws;
  }

  get systemSize(): number {
    return this.getRowCount();
  }

  get price(): number {
    return this.getRowCount() * this._rowPrice * this._numberOfDraws;
  }

  get rowPriceMulti(): number {
    const [minPrice] = this.productConfig.rowPrice;
    return this.rowPrice / minPrice;
  }

  get itsProductId(): (typeof ITS_PRODUCT_ID)[Product] {
    return ITS_PRODUCT_ID[this._product];
  }

  get productName(): string {
    // TODO switch this to display name for products when implemented
    if (this._subProduct === 'EmTipset') {
      return 'EM-Tipset';
    }

    if (this._subProduct === 'VmTipset') {
      return 'VM-Tipset';
    }

    return PRODUCT_NAME[this._product];
  }

  get betProperties(): BetProperty[] {
    return [...this._betProperties] as const;
  }

  get isOwnRows(): boolean {
    return this._betProperties.has('EGNARADER') || this._betProperties.has('REDUCERA_FRITT');
  }

  /**
   * Gets if board is only single rows. Only used for own rows.
   *
   * @returns Boolean | If board is only single rows
   */
  get isSingleRowsOnly(): boolean {
    return this.boards.some((board) => board.systemType === 'SROW');
  }

  get isQuickPick(): boolean {
    return Boolean(this.quickPickValue);
  }

  get quickPickValue(): QuickPick | XpertenBetProperty | undefined {
    const isXperten = this._betProperties.has('XPERTEN');

    if (isXperten) {
      return 'XPERTEN';
    }

    for (const board of this.boards) {
      for (const boardProp of board.boardProperties) {
        if (isQuickPick(boardProp)) {
          return boardProp;
        }
      }
    }

    return undefined;
  }

  protected get productConfig(): ProductConfig {
    return getProductConfig(this._product);
  }

  protected get priceAsJupiterString(): string {
    return this.price.toFixed(2).replace(',', '.');
  }

  /**
   * Adds a bet selection of outcomes to the bet.
   * The format of the outcome selections is generic so if the format doesn't suite the composition of the wager it should be handled within the `compose` function of each bet subclass.
   *
   * @param boardBuilderFn If a number is provided it is considered a binary number and will convert to an array of indices for each bit that is set.
   * Either if it is an array of numbers it is considered to already be an array of bits indices that is set
   * @returns - The updated Bet instance.
   */
  addBoard(boardBuilderFn: (board: TBoard) => void): this {
    const board = this.getBoard();

    boardBuilderFn(board);

    this._boards.push(board);

    return this;
  }

  /**
   * Set the subProductId for the bet.
   *
   * @param subProduct - The subProduct to set
   * @returns The updated Bet instance.
   */
  setSubProduct(subProduct: SubProduct): this {
    this._subProduct = subProduct;

    return this;
  }

  /**
   * Adds a bet property to the bet instance, can either be for marketplace, competition etc.
   *
   * @param betProperty - The bet property to add.
   * @returns The updated Bet instance.
   */
  addBetProperty(betProperty: BetProperty): this {
    // Assemble valid properties to validate against
    // const validProperties = Object.values(Bet.BetProperties);

    // if (!validProperties.includes(betProperty)) {
    //   throw new WagerError({
    //     message: `Invalid board property: ${betProperty}, valid: [${validProperties.join(', ')}]`,
    //     clientCode: WagerError.ErrorCodes.UNSUPPORTED_VALUE_BET_PROP,
    //   });
    // }

    this._betProperties.add(betProperty);

    return this;
  }

  /**
   * Update the row price of the bet.
   *
   * @param rowPrice - The updated row price
   * @throws {WagerError} If the row prise is invalid.
   * @returns The updated Bet instance.
   */
  setRowPrice(rowPrice: number): this {
    const [minPrice, maxPrice] = this.productConfig.rowPrice;

    if (rowPrice % minPrice !== 0) {
      throw new WagerError({
        message: `Invalid row price: ${rowPrice}, min: ${minPrice}, max: ${maxPrice}. Should be evenly divided with minPrice`,
        clientCode: 'UNSUPPORTED_VALUE_ROW_PRICE',
      });
    }

    if (rowPrice < minPrice || rowPrice > maxPrice) {
      throw new WagerError({
        message: `Invalid row price: ${rowPrice}, min: ${minPrice}, max: ${maxPrice}`,
        clientCode: 'UNSUPPORTED_VALUE_ROW_PRICE',
      });
    }

    this._rowPrice = rowPrice;

    return this;
  }

  /**
   * Set the draw number that the bet relates to.
   *
   * @param drawNumber - The draw number that the bet relates to
   * @returns The updated Bet instance.
   */
  setDrawNumber(drawNumber: number): this {
    this._drawNumber = drawNumber;

    return this;
  }

  /**
   * Set the draw count of how many times the bet should be placed.
   *
   * @param numberOfDraws - How many times that the bet should be placed
   * @returns The updated Bet instance.
   * @throws {WagerError} If the draw count is invalid
   */
  setNumberOfDraws(numberOfDraws: number): this {
    if (!this.productConfig.numberOfDraws.includes(numberOfDraws)) {
      throw new WagerError({
        message: `Invalid draw count: ${numberOfDraws}, valid: ${this.productConfig.numberOfDraws.join(', ')}`,
        clientCode: 'UNSUPPORTED_VALUE_DRAW_COUNT',
      });
    }
    this._numberOfDraws = numberOfDraws;

    return this;
  }

  /**
   * Returns the total amount of rows for all boards.
   *
   * @returns Rows
   */
  getRowCount(): number {
    return this.boards.reduce((sum, board) => board.getRowCount() + sum, 0);
  }

  /**
   * Used when the Wager is composed to ITS payable wager object.
   *
   * @returns ITS Wager
   */
  compose(): ITSPayloadBet<object> {
    const boards = this._getBoards();

    const additionalBetProperties: { betProperties?: readonly BetProperty[] } = {};
    if (this.betProperties.length > 0) {
      additionalBetProperties.betProperties = this.betProperties;
    }

    const itsBet = Object.assign(
      {
        productId: this.itsProductId,
        drawNum: this.drawNumber,
        numDraws: this.numberOfDraws,
        betAmountSalesGroup1: this.priceAsJupiterString,
        betAmountSalesGroup2: '',
        rowPriceMulti: this.rowPriceMulti,
        ...additionalBetProperties,
      },
      boards
    );

    // type ITSBet = typeof itsBet;

    return itsBet;
  }

  /**
   * Creates a compact string containing relevant information about the bet to be logged.
   * Exludes boards if external.
   *
   * @returns String to log
   */
  toString(): string {
    const boardsStrings =
      this.isOwnRows || (this.boards.length > 1 && this.boards[0]?.systemType === 'SROW')
        ? [`${this.boards[0]?.systemType}:(${this.systemSize})`]
        : this.boards.map((board) => board.toString(this.isOwnRows));

    const logEntries = [
      `pID:${this.product}`,
      `D:${this.drawNumber}`,
      boardsStrings.join('|'),
      `RP:${this.rowPrice}`,
      `BP:${this.betProperties.join('|')}`,
      `R:${this.numberOfDraws}`,
    ];

    return logEntries.join(' ');
  }

  private _getBoards(): ITSPayloadBet<object> {
    if (this.isOwnRows) {
      return this.composeOwnRowsBoards();
    }

    return this.composeBoards();
  }

  /**
   * Returns a prettified version of the system, eg. M 64
   *
   * @returns System as string
   */
  abstract getSystemLabel(): string;

  /**
   * The function should be implemented of a subclass of Bet.
   * It should return the composed version of boards
   */
  protected abstract composeBoards(): ITSPayloadBet<object>;

  /**
   * The function should be implemented of a subclass of Bet.
   * It should return the composed version of external boards
   */
  protected abstract composeOwnRowsBoards(): ITSPayloadBet<object>;

  /**
   * Returns a new instance of the board
   *
   * @returns Board instance
   */
  protected abstract getBoard(): TBoard;
}

export default Bet;
